import React, { useState } from 'react';
import { getImageUrl, getPlaceholder } from '../api';
import ConstructionCanvas from './ConstructionCanvas';

const CRANE = '#d39816';

export function ProjectCard({ project, onClick }) {
  const imgUrl = project.thumbnail
    ? getImageUrl('projects', project.thumbnail)
    : getPlaceholder(800, 600, project.title);
  return (
    <div className="project-card" style={{ cursor: 'pointer' }} onClick={onClick}>
      <div className="project-card-img-wrap">
        <img src={imgUrl} alt={project.title} className="project-card-img"
          onError={e => { e.target.src = getPlaceholder(800, 600, project.title); }} />
      </div>
      <div className="project-card-body">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span style={{ background: `rgba(211,152,22,0.12)`, color: CRANE, fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, border: `1px solid rgba(211,152,22,0.25)` }}>
            {project.status?.charAt(0).toUpperCase() + project.status?.slice(1)}
          </span>
          {project.location && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              <i className="bi bi-geo-alt me-1" />{project.location}
            </span>
          )}
        </div>
        <h5 style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', marginBottom: 8, fontSize: '1.1rem' }}>
          {project.title}
        </h5>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', lineHeight: 1.7, marginBottom: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {project.short_description || project.description}
        </p>
        {project.client_name && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)' }}>
            <i className="bi bi-person me-1" style={{ color: CRANE }} />{project.client_name}
          </div>
        )}
      </div>
    </div>
  );
}

export function ServiceCard({ service }) {
  return (
    <div className="service-card h-100">
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
        <div className="service-icon-wrap" style={{ marginBottom: 0 }}>
          <i className={`bi ${service.icon}`} style={{ color: service.icon_color || CRANE }} />
        </div>
        <h5 style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', margin: 0 }}>{service.name}</h5>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.8, marginBottom: 0 }}>{service.description}</p>
    </div>
  );
}

export function Loader({ text = 'Loading...' }) {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-3">
      <div className="spinner dark" />
      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{text}</p>
    </div>
  );
}

export function EmptyState({ icon = 'bi-inbox', title = 'Nothing here yet', subtitle = '' }) {
  return (
    <div className="text-center py-5">
      <i className={`bi ${icon}`} style={{ fontSize: 44, color: 'var(--border)', marginBottom: 14, display: 'block' }} />
      <h5 style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-display)', marginBottom: 6 }}>{title}</h5>
      {subtitle && <p style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>{subtitle}</p>}
    </div>
  );
}

export function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 32, maxWidth: 420, width: '100%', boxShadow: 'var(--shadow-xl)' }}>
        <div className="text-center mb-4">
          <div style={{ width: 54, height: 54, borderRadius: '50%', background: '#FDECEA', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <i className="bi bi-exclamation-triangle" style={{ fontSize: 22, color: 'var(--danger)' }} />
          </div>
          <h5 style={{ color: 'var(--text)', marginBottom: 8 }}>Confirm Delete</h5>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7 }}>{message}</p>
        </div>
        <div className="d-flex gap-3 justify-content-center">
          <button onClick={onCancel} className="btn-outline-secondary-styled">Cancel</button>
          <button onClick={onConfirm} style={{ background: 'var(--danger)', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 'var(--radius)', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
            <i className="bi bi-trash me-1" />Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export function Lightbox({ images, index, onClose }) {
  const [current, setCurrent] = useState(index);
  return (
    <div className="lightbox" onClick={onClose}>
      <button className="lightbox-close" onClick={onClose}><i className="bi bi-x-lg" /></button>
      {images.length > 1 && (
        <button className="lightbox-nav prev" onClick={e => { e.stopPropagation(); setCurrent(i => (i - 1 + images.length) % images.length); }}>
          <i className="bi bi-chevron-left" />
        </button>
      )}
      <img src={images[current]} alt="" onClick={e => e.stopPropagation()} />
      {images.length > 1 && (
        <button className="lightbox-nav next" onClick={e => { e.stopPropagation(); setCurrent(i => (i + 1) % images.length); }}>
          <i className="bi bi-chevron-right" />
        </button>
      )}
      <div style={{ position: 'absolute', bottom: 18, color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
        {current + 1} / {images.length}
      </div>
    </div>
  );
}

// PageHero — supports optional hero image; falls back to light bg when no image given
export function PageHero({ label, title, accent, subtitle, image, pageClass }) {
  if (image) {
    // ── Photo-backed hero (dark overlay + white text) ──────────
    const modClass = pageClass ? ` page-hero-photo--${pageClass}` : '';
    return (
      <div className={`page-hero-photo${modClass}`} style={{ padding: '180px 0 80px', position: 'relative', overflow: 'hidden', background: '#141420', minHeight: 580 }}>
        {/* Background photo */}
        <picture>
          <source srcSet={image.replace(/\.jpg$/, '.webp')} type="image/webp" />
          <img
            src={image} alt=""
            fetchpriority="high"
            onError={e => { e.target.style.display = 'none'; }}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
          />
        </picture>
        {/* Dark gradient overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(105deg,rgba(20,20,30,0.85) 0%,rgba(20,20,30,0.58) 60%,rgba(20,20,30,0.38) 100%)', zIndex: 1 }} />
        {/* Canvas animation on top of overlay */}
        <ConstructionCanvas variant="crane" color={CRANE} opacity={0.35} />
        {/* Content */}
        <div className="container" style={{ position: 'relative', zIndex: 3 }}>
          <span className="page-hero-photo-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 4, textTransform: 'uppercase', color: CRANE, display: 'block', marginBottom: 10, animation: 'fadeInUp 0.5s ease both' }}>
            {label}
          </span>
          <h1 className="page-hero-photo-title" style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.4rem,5vw,3.8rem)', color: '#fff', animation: 'fadeInUp 0.55s 0.08s ease both', textShadow: '0 3px 20px rgba(0,0,0,0.4)' }}>
            {title} {accent && <span style={{ color: CRANE, fontStyle: 'italic' }}>{accent}</span>}
          </h1>
          {subtitle && (
            <p className="page-hero-photo-subtitle" style={{ color: 'rgba(255,255,255,0.65)', maxWidth: 580, marginTop: 14, fontSize: '1.05rem', lineHeight: 1.8, animation: 'fadeInUp 0.6s 0.16s ease both' }}>
              {subtitle}
            </p>
          )}
          <span style={{ display: 'block', width: 56, height: 3, marginTop: 22, background: `linear-gradient(90deg,${CRANE},transparent)`, borderRadius: 2 }} />
        </div>
      </div>
    );
  }

  // ── Original light version (no image) — unchanged ────────────
  return (
    <div style={{ padding: '140px 0 80px', background: 'var(--site-bg-3)', position: 'relative', overflow: 'hidden' }}>
      <ConstructionCanvas variant="crane" color={CRANE} opacity={0.38} />
      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 4, textTransform: 'uppercase', color: CRANE, display: 'block', marginBottom: 10, animation: 'fadeInUp 0.5s ease both' }}>
          {label}
        </span>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.4rem,5vw,3.8rem)', color: 'var(--text)', animation: 'fadeInUp 0.55s 0.08s ease both' }}>
          {title} {accent && <span style={{ color: CRANE, fontStyle: 'italic' }}>{accent}</span>}
        </h1>
        {subtitle && (
          <p style={{ color: 'var(--text-muted)', maxWidth: 580, marginTop: 14, fontSize: '1.05rem', lineHeight: 1.8, animation: 'fadeInUp 0.6s 0.16s ease both' }}>
            {subtitle}
          </p>
        )}
        <span style={{ display: 'block', width: 56, height: 3, marginTop: 22, background: `linear-gradient(90deg,${CRANE},transparent)`, borderRadius: 2 }} />
      </div>
    </div>
  );
}

// Icon picker — admin only
const ICONS = [
  'bi-building','bi-building-fill','bi-house-fill','bi-house-heart-fill','bi-tools',
  'bi-gear','bi-gear-wide-connected','bi-wrench','bi-hammer','bi-brush',
  'bi-pencil-square','bi-rulers','bi-clipboard2-check-fill','bi-people-fill',
  'bi-lightning-charge-fill','bi-droplet-fill','bi-fire','bi-tree-fill',
  'bi-cpu-fill','bi-shield-fill-check','bi-star-fill','bi-trophy-fill',
  'bi-award-fill','bi-diagram-3-fill','bi-layers-fill','bi-grid-fill',
  'bi-bar-chart-fill','bi-graph-up','bi-check-circle-fill','bi-geo-alt-fill',
  'bi-camera-fill','bi-headset','bi-telephone-fill','bi-envelope-fill',
];

export function IconPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(!open)}
        style={{ background: '#fff', border: '1.5px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px', color: 'var(--text)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
        <i className={`bi ${value}`} style={{ fontSize: 20, color: 'var(--c4)' }} />
        <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{value}</span>
        <i className="bi bi-chevron-down ms-auto" style={{ color: 'var(--text-light)' }} />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, zIndex: 200, marginTop: 4, maxHeight: 220, overflowY: 'auto', boxShadow: 'var(--shadow)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 6 }}>
            {ICONS.map(icon => (
              <button key={icon} type="button" title={icon}
                onClick={() => { onChange(icon); setOpen(false); }}
                style={{ background: icon === value ? 'rgba(155,142,199,0.12)' : 'var(--bg-3)', border: icon === value ? '1.5px solid var(--c4)' : '1.5px solid transparent', borderRadius: 'var(--radius)', padding: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: icon === value ? 'var(--c4-dark)' : 'var(--text-muted)', fontSize: 18 }}>
                <i className={`bi ${icon}`} />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
